"use client";

import { useState, type ReactNode } from "react";
import { cn } from "@marketing-sites/utils/cn";
import { Button, Carousel } from "../../primitives";
import { SpeakerArrows } from "./arrows";

export const MOBILE_VISIBLE_SPEAKERS = 4;

// Embla is switched off below md so the slides fall back to a plain grid.
const CAROUSEL_OPTIONS = {
  align: "start",
  watchDrag: true,
  containScroll: "trimSnaps",
  breakpoints: { "(max-width: 767px)": { active: false } },
} as const;

export interface SpeakerListProps {
  cards: { key: string; node: ReactNode }[];
  showMoreLabel: string;
  showMoreMetadata: { analyticsId: string };
}

/** Carousel from md up; a stacked grid with "Show more" on mobile. Cards are rendered by the caller. */
export function SpeakerList({
  cards,
  showMoreLabel,
  showMoreMetadata,
}: SpeakerListProps) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = cards.length > MOBILE_VISIBLE_SPEAKERS;

  return (
    <Carousel.Root
      fade={false}
      autoProgress={false}
      options={CAROUSEL_OPTIONS}
      className="col-span-full flex flex-col gap-y-6"
    >
      <SpeakerArrows className="hidden self-end md:flex" />
      <Carousel.Viewport>
        <Carousel.Container className="grid grid-cols-2 gap-x-4 gap-y-8 md:flex md:gap-4">
          {cards.map(({ key, node }, index) => (
            <Carousel.Slide
              key={key}
              index={index}
              className={cn(
                "md:basis-[calc((100%-2rem)/3.3)] lg:basis-[calc((100%-4rem)/5.3)]",
                !expanded &&
                  index >= MOBILE_VISIBLE_SPEAKERS &&
                  "max-md:hidden",
              )}
            >
              {node}
            </Carousel.Slide>
          ))}
        </Carousel.Container>
      </Carousel.Viewport>
      {hasMore && !expanded && (
        <Button
          variant="secondary"
          analyticsId={showMoreMetadata.analyticsId}
          onClick={() => setExpanded(true)}
          className="self-start md:hidden"
        >
          {showMoreLabel}
        </Button>
      )}
    </Carousel.Root>
  );
}
